import Controller, { OnCatch, OnClick, OnMount, OnSuccess, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { MountEvent, RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LoginAPI } from '@philips-internal/oswald-ui-foundation';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import TokenConstants from '../../constants/TokenConstants';
import { ModalButtonType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { HttpError } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { MessageService, HttpService, AuthenticationService, JWTToken } from '@philips-internal/oswald-ui-core/lib/services';
import { ModalService, ThemeTonalRange, ThemeService, ModalButton } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import './index.css';
import { BaseAPIState } from '@philips-internal/oswald-ui-core';
import { FragmentNames } from '../../codegen';

interface Resp {
  [key: string]: string;
}

interface ThemeLogin {
  dsCorLogin: string;
  dsLogoLogin: string;
}

interface RequestEventLogin {
  preventDefault: () => void;
}

interface CheckTermUse {
  termSeqNr: number;
}

interface TermsUse {
  termSeqNr: number;
}

@Controller({ fragment: 'login', component: 'login-panel' })
class LoginController extends BaseController {

  @Inject('login')
  login: LoginAPI;

  private username: string;

  @OnClick({ target: ['new-user-button'] })
  onClick(): void {
    this.historyService.push(APPLICATION_ROUTES.NEW_USER);
  }

  @OnMount({ target: 'login-panel' })
  onMountLogin(): void {
    const { loginContentTonalRange = ThemeTonalRange.BRIGHT } = getFoundationDataContext().getBulkData();
    ThemeService.setContentTonalRange(loginContentTonalRange as ThemeTonalRange);
    HttpService.get(APPLICATION_ENDPOINTS.CHECK_ASSIGN_TERMS).then((result) => {
      if (!result.body) {
        AuthenticationService.logout();
      }
    });
  }

  @OnUnmount({ target: 'login-panel' })
  onUnmountLogin(): void {
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
  }

  @OnMount({ target: ['login'] })
  onMount(event: MountEvent): void {
    const api = event.target as unknown as LoginAPI;

    HttpService.get(APPLICATION_ENDPOINTS.CONFIGURATION)
      .then(response => {
        const { dsLogoLogin } = response.body as ThemeLogin;
        api.setLogo(dsLogoLogin);
      })
  }

  @OnCatch({ target: ['login']})
  onCatch(event: RequestEvent<Error | Record<string, string>>): void {
    const target = event.target as unknown as BaseAPIState;
    const state = target.state as { username: string };
    const api = target as unknown as LoginAPI;
    const error = event.response as unknown as HttpError;

    if (error.response != null) {
      error.response.json()
        .then((resp: Resp) => {
          const message: string = this.getErrorMessage(resp);
          this.username = state.username;
          if (message != null && message != '') {
            api.setNotification(
              {
                highlight: true,
                level: ToastLevel.ERROR,
                message,
                title: ' ',
              }
            );
          }
        })
    }
  }

  getErrorMessage(resp: Resp): string {
    let message = Object.keys(resp).filter(item => item !== '1').reduce((ret, value) => `${ret} ${MessageService.translate(resp[value])}`, '');
    if (Object.keys(resp).find((key) => key === '1') != null) {
      const remainingAttempts = Object.entries(resp)[0];
      message = message.replace('#@QT_TENT_REST#@', remainingAttempts[1])
    }

    if (Object.keys(resp).find(item => item === '870103' ) != null) {
      message = '';
      this.openModalWarning();
    }
    return message;
  }

  openRenewPasswordModal(username: string): void {
    const modalId = FragmentNames.CHANGE_EXPIRED_PASSWORD;
    ModalService.openDefault({
      id: modalId,
      closeable: true,
      closeByDocument: false,
      className: 'index',
      fragment: modalId,
      title: 'results.portal-title-renew-password',
      contextData: { username },
      onSubmit: () => '',
      onCancel: () => '',
      theme: ThemeTonalRange.ULTRA_LIGHT
    });
  }

  openModalWarning(): void {
    const buttonConfirm: ModalButton[] = [
      {
        action: () => { this.openRenewPasswordModal(this.username) },
        title: 'text.button-ok',
        type: ModalButtonType.SUCCESS,
      },
    ];
    ModalService.openDefault({
      closeable: true,
      closeByDocument: false,
      fragment: 'modal-warning-expiration-password',
      title: 'warning.text-atention',
      dockButtons: buttonConfirm,
      theme: ThemeTonalRange.ULTRA_LIGHT
    });
  }

  @OnSuccess({ target: ['login'] })
  onSuccess(event: RequestEventLogin): void {
    if (!event.preventDefault) {
      return;
    }
    event.preventDefault();

    const token = AuthenticationService.getJWTToken() as JWTToken;
    const cdPhysicalPerson: string = token.claims[TokenConstants.CD_PHYSICAL_PERSON] as string;

    HttpService.get(APPLICATION_ENDPOINTS.CHECK_TERMS_OF_USE).then((result) => {
      const checkTerm = result.body as CheckTermUse;
      if (!checkTerm.termSeqNr) {
        HttpService.get(APPLICATION_ENDPOINTS.GET_TERMS_OF_USE_SEQUENCE).then(resp => {
          const termSeqNr = (resp.body as TermsUse).termSeqNr;
          this.context.set({ termSeqNr });
          this.historyService.push(APPLICATION_ROUTES.ASSIGN_TERMS_OF_USE, { termSeqNr });
        });
      }
      else {
        HttpService.get(APPLICATION_ENDPOINTS.CHECK_PRIVACY).then(resp => {
          const checkPrivacy = resp.body as CheckTermUse;
          if (!checkPrivacy.termSeqNr) {
            HttpService.get(APPLICATION_ENDPOINTS.GET_PRIVACY_SEQUENCE).then(respAviso => {
              const privacySeqNr = (respAviso.body as TermsUse).termSeqNr;
              this.context.set({ privacySeqNr });
              this.historyService.push(APPLICATION_ROUTES.ASSIGN_PRIVACY, { privacySeqNr });
            });
          }
          else {
            this.historyService.push(APPLICATION_ROUTES.EXAMS_RESULTS, { cdPhysicalPerson });
          }
        });
      }
    });
  }

}

export default LoginController;
